import type { TFunction } from "i18next";

import type { TransportCircuit } from "src/api/client-api/transport/useTransportCircuits";
import type { TransportLeg } from "src/api/client-api/transport/useTransportLegs";

export type TransportRouteEndpoints = {
    origin: string;
    destination: string;
};

const distanceModeLabel = (leg: TransportLeg, t: TFunction): string => {
    switch (leg.distanceMode) {
        case "COMPUTED_FROM_ROUTE":
            return t("transport.meta.precise_route", "Precise route");
        case "COMPUTED_FROM_COORDS":
            return t("transport.meta.straight_line", "Straight-line distance");
        case "COPIED_FROM_REFERENCE":
            return t("transport.meta.reference_distance", "Reference distance");
        case "MANUAL":
        default:
            return t("transport.meta.manual_distance", "Manual distance");
    }
};

export const getTransportLegMeta = (leg: TransportLeg, t: TFunction): string[] => [
    leg.distanceKm != null ?
        `${leg.distanceKm.toFixed(1)} km`
    :   t("transport.meta.distance_unknown", "Distance unavailable"),
    distanceModeLabel(leg, t),
    ...(leg.isRefrigerated ? [t("transport.mode.refrigerated", "Refrigerated")] : []),
];

const getCircuitTotalDistance = (circuit: TransportCircuit): number | null => {
    const legs = circuit.circuitLegs.map((circuitLeg) => circuitLeg.transportLeg);
    if (legs.length === 0) {
        return null;
    }

    const distances = legs.map((leg) => (typeof leg === "object" && leg !== null ? leg.distanceKm : null));
    if (!distances.every((distance): distance is number => distance != null)) {
        return null;
    }

    return distances.reduce((total, distance) => total + distance, 0);
};

const circuitLegCountLabel = (count: number, t: TFunction): string =>
    count === 1 ?
        `${count} ${t("transport.circuit.leg_label", "leg")}`
    :   `${count} ${t("transport.circuit.legs_label", "legs")}`;

export const getTransportCircuitEndpoints = (circuit: TransportCircuit): TransportRouteEndpoints => {
    const orderedCircuitLegs = circuit.circuitLegs.toSorted((a, b) => a.orderIndex - b.orderIndex);
    const firstLeg = orderedCircuitLegs[0]?.transportLeg;
    const lastLeg = orderedCircuitLegs.at(-1)?.transportLeg;

    return {
        origin:
            typeof firstLeg === "object" && firstLeg !== null ?
                (firstLeg.originLabel ?? firstLeg.originCountryCode ?? circuit.originCountryCode ?? "-")
            :   (circuit.originCountryCode ?? "-"),
        destination:
            typeof lastLeg === "object" && lastLeg !== null ?
                (lastLeg.destinationLabel ?? lastLeg.destinationCountryCode ?? circuit.destinationCountryCode ?? "-")
            :   (circuit.destinationCountryCode ?? "-"),
    };
};

export const isLinkedReferenceCircuitWithoutLocalGeometry = (circuit: TransportCircuit): boolean =>
    circuit.linkMode === "LINKED" && circuit.sourceReferenceCircuitId != null && circuit.circuitLegs.length === 0;

export const getTransportCircuitMeta = (circuit: TransportCircuit, t: TFunction): string[] => {
    if (isLinkedReferenceCircuitWithoutLocalGeometry(circuit)) {
        return [t("transport.meta.reference_itinerary", "Reference itinerary")];
    }

    const totalDistanceKm = getCircuitTotalDistance(circuit);

    return [
        totalDistanceKm != null ?
            `${totalDistanceKm.toFixed(1)} km`
        :   t("transport.meta.distance_unknown", "Distance unavailable"),
        circuitLegCountLabel(circuit.circuitLegs.length, t),
    ];
};
